import type { QueryKey, UseQueryOptions } from '@tanstack/react-query';
import {
  keepPreviousData,
  useQuery,
} from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import type {
  PaginationFetcher,
  PaginationInterface,
  PaginationResult,
} from './types';

type Props<T> = {
  fetcher: PaginationFetcher<T>;
  queryKey: QueryKey;
  pageSize?: number;
  initialPage?: number;
  queryOptions?: Omit<
    UseQueryOptions<
      PaginationResult<T>,
      unknown,
      PaginationResult<T>,
      QueryKey
    >,
    'queryKey' | 'queryFn'
  >;
  onSuccess?: (data: PaginationResult<T>) => void;
};

const usePaginatedQuery = <T extends unknown>(props: Props<T>) => {
  const {
    fetcher,
    queryKey,
    pageSize = 10,
    initialPage = 1,
    queryOptions,
  } = props;

  const [currentPage, setCurrentPage] = useState(initialPage);

  const queryKeys = useMemo(() => {
    return [...(Array.isArray(queryKey) ? queryKey : [queryKey])];
  }, [queryKey]);

  useEffect(() => {
    setCurrentPage(initialPage);
  }, [JSON.stringify(queryKeys)]);

  const query = useQuery({
    queryKey: [...queryKeys, currentPage, pageSize],
    queryFn: () => fetcher({ page: currentPage, pageSize }),
    placeholderData: keepPreviousData,
    ...queryOptions,
  } as UseQueryOptions<
    PaginationResult<T>,
    unknown,
    PaginationResult<T>,
    QueryKey
  >);

  useEffect(() => {
    if (query.data) {
      props.onSuccess?.(query.data);
    }
  }, [query.data]);

  const totalPages = useMemo(() => {
    if (!query.data?.totalCount) return 0;
    return Math.ceil(query.data.totalCount / pageSize);
  }, [query.data?.totalCount, pageSize]);

  const pagination: PaginationInterface = useMemo(
    () => ({
      currentPage,
      pageSize,
      totalCount: query.data?.totalCount || 0,
      totalPages,
      hasNextPage: currentPage < totalPages,
      hasPreviousPage: currentPage > 1,
      goToPage: (page: number) => {
        if (page >= 1 && page <= totalPages) {
          setCurrentPage(page);
        }
      },
      goToNextPage: () => {
        if (currentPage < totalPages) {
          setCurrentPage(currentPage + 1);
        }
      },
      goToPreviousPage: () => {
        if (currentPage > 1) {
          setCurrentPage(currentPage - 1);
        }
      },
      reset: () => setCurrentPage(initialPage),
    }),
    [currentPage, pageSize, totalPages, initialPage, query.data?.totalCount],
  );

  return {
    data: query.data?.data || [],
    totalCount: query.data?.totalCount || 0,
    pagination,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    isSuccess: query.isSuccess,
  };
};

export default usePaginatedQuery;
